'use client'

import { createContext, type ReactNode, useContext, useMemo } from 'react'

type ChatBrandContextValue = {
  /**
   * The brand THIS thread is bound to — `useConvexChatRuntime`'s
   * `effectiveBrandId`, the same id its messages were loaded under.
   */
  brandId: string | undefined
}

const ChatBrandContext = createContext<ChatBrandContextValue | null>(null)

/**
 * Publishes the thread's own brand to the cards inside it.
 *
 * A brand-EXTRACTION chat is bound to a `brands` row that is NOT necessarily
 * the globally active brand, and `/chat/<id>` renders such a thread without
 * switching the active brand. Cards that subscribe to a live brand row must
 * therefore follow the TRANSCRIPT's brand, or a historical thread shows the
 * active brand's images, identity, counts and design guide beside another
 * brand's tool calls.
 *
 * Outside a thread (the review page, a card rendered standalone) there is no
 * provider and consumers fall back to the active brand, which is what those
 * surfaces mean.
 */
export function ChatBrandProvider({
  brandId,
  children,
}: {
  brandId: string | undefined
  children: ReactNode
}) {
  const value = useMemo(() => ({ brandId }), [brandId])
  return (
    <ChatBrandContext.Provider value={value}>
      {children}
    </ChatBrandContext.Provider>
  )
}

/** The thread's brand id, or `undefined` outside a thread. */
export function useChatBrandId(): string | undefined {
  return useContext(ChatBrandContext)?.brandId
}
